<script setup>
import { useRouter } from 'vue-router';
const emit = defineEmits(['close']);

const { project } = defineProps({
  project: Object,
});
const closeModal = () => {
  emit('close');
};
const router = useRouter();

const goToProjectPage = (id) => {
  closeModal();
  router.push({ name: 'ProjectDetailView', params: { id } }).catch(err => {
    console.error('Navigation error:', err);
  });
};
</script>

<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-content">
      <button class="close-button" @click="closeModal" aria-label="Close">×</button>

      <div class="modal-body">
        <div class="project-text">
          <header class="project-header">
            <p class="project-eyebrow">{{ project.subtitle }}</p>
            <h2 class="project-title">{{ project.title }}</h2>
            <p class="project-type">{{ project.type }}</p>
          </header>

          <p class="project-description">{{ project.description }}</p>
          <p class="project-description">{{ project.subdescription }}</p>

          <div class="stack-section">
            <h3 class="stack-heading">Stack</h3>
            <ul class="stack-list">
              <li v-for="(stack, index) in project.stack" :key="index">{{ stack }}</li>
            </ul>
          </div>

          <button @click="goToProjectPage(project.id)" class="full-page-btn">
            See full page →
          </button>
        </div>

        <div class="project-images">
          <img
            v-for="(image, index) in project.images"
            :key="index"
            :src="image"
            :alt="project.title"
            class="project-image"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  --ink: #16161a;
  --muted: #6b6b70;
  --line: #e6e5e2;
  --radius: 10px;

  position: fixed;
  inset: 0;
  background: rgba(20, 20, 22, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 200;
  padding: 24px;
}

.modal-content {
  position: relative;
  background: #fff;
  color: var(--ink);
  width: 900px;
  max-width: 100%;
  max-height: 85vh;
  border-radius: var(--radius);
  overflow-y: auto;
  padding: 40px;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: var(--muted);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.15s, color 0.15s;
}
.close-button:hover { background: var(--line); color: var(--ink); }

.modal-body {
  display: flex;
  align-items: center;
  gap: 32px;
}

.project-text {
  flex: 1.4;
  min-width: 0;
  text-align: center;
}

.project-header {
  border-bottom: 1px solid var(--line);
  padding-bottom: 16px;
  margin-bottom: 20px;
}

.project-eyebrow {
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin: 0 0 6px;
}

.project-title {
  font-size: 28px;
  font-weight: 600;
  line-height: 1.15;
  margin: 0 0 6px;
}

.project-type {
  font-style: italic;
  color: var(--muted);
  font-size: 14px;
  margin: 0;
}

.project-description {
  font-size: 15px;
  line-height: 1.65;
  color: #333;
  margin: 0 0 14px;
}

.stack-section {
  margin: 24px 0;
}

.stack-heading {
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin: 0 0 10px;
}

.stack-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.stack-list li {
  font-size: 13px;
  padding: 4px 12px;
  border: 1px solid var(--line);
  border-radius: 20px;
  color: #444;
}

.full-page-btn {
  border: 1px solid var(--ink);
  background: var(--ink);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 18px;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.15s;
}
.full-page-btn:hover { opacity: 0.85; }

.project-images {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 70vh;
  overflow-y: auto;
}

.project-image {
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
}

@media (max-width: 768px) {
  .modal-content { padding: 24px; max-height: 90vh; }
  .modal-body { flex-direction: column; }
  .project-images { display: none; }
  .project-title { font-size: 22px; }
}
</style>
